import { FunctionComponent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { faListOl, faUsers } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";

export interface DeepDiveCampaignRef {
	listId: number;
	listName: string;
	campaignId: string;
	campaignName: string;
}

export interface DeepDiveActionsProps {
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	onOpenCampaign: (campaign: DeepDiveCampaignRef) => void;
	onOpenLeads: (listId: number | null) => void;
}

/** Every active campaign across the selection, flattened with its list. */
export const selectedCampaignRefs = (
	selectedLists: CustomListsAutocompleteForSequencerItem[]
): DeepDiveCampaignRef[] =>
	selectedLists.flatMap((list) =>
		(list.campaigns ?? [])
			.filter((campaign) => campaign.active)
			.map((campaign) => ({
				listId: list.id as number,
				listName: list.title,
				campaignId: campaign.campaignId,
				campaignName: campaign.campaignName || campaign.campaignId,
			}))
	);

/**
 * A button that acts directly when the selection names exactly one thing, and otherwise
 * explains itself and offers the choices — the mockup's disabled-with-popover pattern.
 */
const ActionButton: FunctionComponent<{
	icon: IconDefinition;
	label: string;
	/** Acts directly. When absent the button is inert and only opens the popover. */
	onClick?: () => void;
	popover?: ReactNode;
}> = ({ icon, label, onClick, popover }) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div
			className="relative flex-1"
			onMouseEnter={() => setIsOpen(true)}
			onMouseLeave={() => setIsOpen(false)}
		>
			<button
				className={classNames(
					"flex flex-row items-center justify-center gap-2 w-full h-9 rounded-xl border text-xs font-semibold duration-200",
					onClick
						? "cursor-pointer border-gray-200 text-text-meta hover:border-brand-light hover:text-brand-light"
						: "cursor-default border-gray-200 text-text-disabled"
				)}
				disabled={!onClick}
				type="button"
				onClick={onClick}
			>
				<Icon className="w-3 h-3" icon={icon} />
				{label}
			</button>

			{popover && isOpen ? (
				<div className="absolute left-0 top-[calc(100%+6px)] z-50 w-[330px] rounded-xl border border-border-light bg-brand-white p-3 shadow-lg">
					{popover}
				</div>
			) : null}
		</div>
	);
};

const PopoverRow: FunctionComponent<{
	title: string;
	subtitle?: string;
	actionLabel: string;
	onClick: () => void;
}> = ({ title, subtitle, actionLabel, onClick }) => (
	<div className="flex flex-row items-center gap-2 border-t border-border-light py-1.5 first:border-t-0">
		<span className="flex-1 min-w-0">
			<span className="block truncate text-xs font-semibold text-text">{title}</span>
			{subtitle ? (
				<span className="block truncate text-[10px] text-text-meta">{subtitle}</span>
			) : null}
		</span>
		<button
			className="shrink-0 cursor-pointer rounded-lg border border-gray-200 px-2 py-1 text-[11px] font-semibold text-text-meta duration-200 hover:border-brand-light hover:text-brand-light"
			type="button"
			onClick={onClick}
		>
			{actionLabel}
		</button>
	</div>
);

/**
 * The two actions under a Campaign Deep Dive picker: view the campaign's steps, and open
 * its leads.
 *
 * Each acts directly when the selection is unambiguous. When it names several, the button
 * goes inert and a popover explains why and lists the choices — a single button cannot
 * open two campaigns, and picking one silently would be a guess.
 */
export const DeepDiveActions: FunctionComponent<DeepDiveActionsProps> = ({
	selectedLists,
	onOpenCampaign,
	onOpenLeads,
}) => {
	const { t } = useTranslation();

	const campaigns = selectedCampaignRefs(selectedLists);
	const lists = selectedLists.filter((list) => typeof list.id === "number");
	const singleCampaign = campaigns.length === 1 ? campaigns[0] : null;
	const singleListId = lists.length === 1 ? (lists[0].id as number) : null;

	return (
		// Equal-width halves so the pair reads as one control strip under the hero tiles,
		// rather than two buttons sized by their labels.
		<div className="flex flex-row gap-2 w-full" data-testid="deep-dive-actions">
			<ActionButton
				icon={faListOl}
				label={t("analytics.actions.viewCampaign")}
				popover={
					campaigns.length > 1 ? (
						<>
							<p className="mb-2 text-[11px] leading-relaxed text-text-meta">
								{t("analytics.actions.viewCampaignMulti")}
							</p>
							{campaigns.map((campaign) => (
								<PopoverRow
									key={`${campaign.listId}-${campaign.campaignId}`}
									actionLabel={t("analytics.actions.viewCampaign")}
									subtitle={campaign.listName}
									title={campaign.campaignName}
									onClick={() => onOpenCampaign(campaign)}
								/>
							))}
						</>
					) : undefined
				}
				onClick={singleCampaign ? () => onOpenCampaign(singleCampaign) : undefined}
			/>

			<ActionButton
				icon={faUsers}
				label={t("analytics.actions.viewLeads")}
				popover={
					lists.length > 1 ? (
						<>
							<p className="mb-2 text-[11px] leading-relaxed text-text-meta">
								{t("analytics.actions.viewLeadsMulti")}
							</p>
							{lists.map((list) => (
								<PopoverRow
									key={list.id}
									actionLabel={t("analytics.actions.viewLeads")}
									title={list.title}
									onClick={() => onOpenLeads(list.id as number)}
								/>
							))}
							<PopoverRow
								actionLabel={t("analytics.actions.viewLeads")}
								subtitle={t("analytics.actions.allListsSubtitle")}
								title={t("analytics.actions.allLists")}
								onClick={() => onOpenLeads(null)}
							/>
							<p className="mt-2 rounded-lg border border-status-warning-light bg-status-warning-light/30 px-2 py-1.5 text-[10px] leading-relaxed text-text-meta">
								{t("analytics.actions.allListsWarning")}
							</p>
						</>
					) : undefined
				}
				onClick={singleListId === null ? undefined : () => onOpenLeads(singleListId)}
			/>
		</div>
	);
};
